import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';

const statusTranslations: Record<string, string> = {
  rejected: 'ОТКЛОНЕНА',
  sent: 'ОТПРАВЛЕН',
  draft: 'ЧЕРНОВИК',
  ready: 'ГОТОВ',
  pending: 'ОЖИДАЕТ',
  failed: 'ОШИБКА',
};

const reportTypeTranslations: Record<string, string> = {
  REJECTION: 'Отклонение',
  TEST_CONFIRMATION: 'Подтверждение теста',
  ANNUAL_REPORT: 'Годовой отчёт',
  INCIDENT_REPORT: 'Отчёт об инциденте',
};

const getStatusColor = (
  status: string
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const colors: Record<
    string,
    'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  > = {
    rejected: 'error',
    sent: 'success',
    draft: 'default',
    ready: 'info',
    pending: 'warning',
    failed: 'error',
  };
  return colors[status] || 'default';
};

const formatDate = (dateString: string): string => {
  if (!dateString || dateString === 'Invalid Date') return 'Н/Д';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Неверная дата';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface Report {
  id: number;
  document_number?: string;
  vessel_name: string;
  mmsi: string;
  ssas_number: string;
  imo_number: string;
  owner_name?: string;
  coordinates?: string; // Координаты сигнала
  signal_time?: string; // Время получения сигнала
  report_type: string;
  created_at: string;
  status: string;
  sent_at?: string;
  sent_by?: string;
  operator_name?: string;
  recipient_email?: string;
}

interface ReportDetailsDialogProps {
  report: Report | null;
  open: boolean;
  onClose: () => void;
  onDownload: (id: number) => void;
}

const ReportDetailsDialog: React.FC<ReportDetailsDialogProps> = ({
  report,
  open,
  onClose,
  onDownload,
}) => {
  if (!report) return null;

  const handleDownload = () => {
    onDownload(report.id);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Детали отчёта № {report.document_number || report.id}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Данные о судне */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              Данные о судне
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Название судна
            </Typography>
            <Typography variant="body1">{report.vessel_name || '—'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Номер терминала SSAS
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {report.ssas_number || '—'}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2" color="textSecondary">
              MMSI
            </Typography>
            <Typography variant="body1">{report.mmsi || '—'}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2" color="textSecondary">
              IMO
            </Typography>
            <Typography variant="body1">{report.imo_number || '—'}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2" color="textSecondary">
              Судовладелец
            </Typography>
            <Typography variant="body1">{report.owner_name || '—'}</Typography>
          </Grid>

          {/* Данные о сигнале */}
          {(report.coordinates || report.signal_time) && (
            <>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  Данные о сигнале
                </Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>
              {report.coordinates && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Координаты
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                    {report.coordinates}
                  </Typography>
                </Grid>
              )}
              {report.signal_time && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Время получения сигнала
                  </Typography>
                  <Typography variant="body1">{report.signal_time}</Typography>
                </Grid>
              )}
            </>
          )}

          {/* Данные о документе */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              Данные о документе
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Тип отчёта
            </Typography>
            <Typography variant="body1">
              {reportTypeTranslations[report.report_type] || 'Неизвестно'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Статус
            </Typography>
            <Chip
              label={statusTranslations[report.status] || report.status}
              color={getStatusColor(report.status)}
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Кем отправлен
            </Typography>
            <Typography variant="body1">
              {report.operator_name
                ? report.operator_name
                : report.sent_by === 'AUTO'
                  ? 'Автоматически'
                  : report.sent_by === 'BATCH'
                    ? 'Пакетная'
                    : report.sent_by || '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Дата создания
            </Typography>
            <Typography variant="body1">{formatDate(report.created_at)}</Typography>
          </Grid>
          {report.sent_at && (
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Дата отправки
              </Typography>
              <Typography variant="body1">{formatDate(report.sent_at)}</Typography>
            </Grid>
          )}
          {report.recipient_email && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Email получателя
              </Typography>
              <Typography variant="body1">{report.recipient_email}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
        {report.status !== 'rejected' && (
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
            Скачать PDF
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportDetailsDialog;
